import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import { 
  Building2, TrendingUp, Users, DollarSign, AlertTriangle,
  BarChart3, PieChart, Target, Clock, RefreshCw, Lock, Unlock
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function PreLeasingDashboardPage() {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [malls, setMalls] = useState([]);
  const [selectedMall, setSelectedMall] = useState("all");
  const [dashboard, setDashboard] = useState(null);
  const [tenantMix, setTenantMix] = useState(null);
  const [blockedUnits, setBlockedUnits] = useState([]);
  const [velocity, setVelocity] = useState(null);

  useEffect(() => {
    fetchMalls();
  }, []);

  useEffect(() => {
    if (token) {
      fetchDashboard();
      fetchBlockedUnits();
      fetchVelocity();
      if (selectedMall !== "all") {
        fetchTenantMix();
      }
    }
  }, [token, selectedMall]);

  const fetchMalls = async () => {
    try {
      const res = await axios.get(`${API}/api/malls`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMalls(res.data);
    } catch (err) {
      console.error("Failed to fetch malls:", err);
    }
  };

  const fetchDashboard = async () => {
    setLoading(true);
    try {
      const url = selectedMall === "all" 
        ? `${API}/api/pre-leasing/dashboard`
        : `${API}/api/pre-leasing/dashboard?mall_id=${selectedMall}`;
      const res = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDashboard(res.data);
    } catch (err) {
      console.error("Failed to fetch dashboard:", err);
      toast.error("Failed to load dashboard");
    } finally {
      setLoading(false);
    }
  };

  const fetchTenantMix = async () => {
    try {
      const res = await axios.get(`${API}/api/pre-leasing/tenant-mix/${selectedMall}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTenantMix(res.data);
    } catch (err) {
      console.error("Failed to fetch tenant mix:", err);
    }
  };

  const fetchBlockedUnits = async () => {
    try {
      const url = selectedMall === "all"
        ? `${API}/api/pre-leasing/units/blocked`
        : `${API}/api/pre-leasing/units/blocked?mall_id=${selectedMall}`;
      const res = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBlockedUnits(res.data);
    } catch (err) {
      console.error("Failed to fetch blocked units:", err);
    }
  };

  const fetchVelocity = async () => {
    try {
      const url = selectedMall === "all"
        ? `${API}/api/pre-leasing/pipeline/velocity`
        : `${API}/api/pre-leasing/pipeline/velocity?mall_id=${selectedMall}`;
      const res = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setVelocity(res.data);
    } catch (err) {
      console.error("Failed to fetch velocity:", err);
    }
  };

  const releaseUnit = async (unitId) => {
    try {
      await axios.post(`${API}/api/pre-leasing/units/${unitId}/release`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Unit released successfully");
      fetchBlockedUnits();
      fetchDashboard();
    } catch (err) {
      toast.error("Failed to release unit");
    }
  };

  const formatCurrency = (val) => {
    if (val >= 10000000) return `₹${(val / 10000000).toFixed(1)}Cr`;
    if (val >= 100000) return `₹${(val / 100000).toFixed(1)}L`;
    return `₹${val?.toLocaleString() || 0}`;
  };

  if (loading && !dashboard) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="pre-leasing-dashboard">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold">Pre-Leasing Dashboard</h1>
          <p className="text-muted-foreground">Asset master, inventory planning & tenant optimization</p>
        </div>
        <Select value={selectedMall} onValueChange={setSelectedMall}>
          <SelectTrigger className="w-[250px]" data-testid="mall-selector">
            <SelectValue placeholder="Select Mall" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Malls</SelectItem>
            {malls.map(mall => (
              <SelectItem key={mall.id} value={mall.id}>{mall.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* KPI Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Vacancy Rate</CardTitle>
            <Building2 className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-500">{dashboard?.vacancy_percent || 0}%</div>
            <Progress value={dashboard?.vacancy_percent || 0} className="mt-2 h-2" />
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Pipeline Value</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatCurrency(dashboard?.pipeline_value)}</div>
            <p className="text-xs text-muted-foreground">{dashboard?.pipeline_count || 0} active deals</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Revenue Forecast</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-500">{formatCurrency(dashboard?.revenue_forecast)}</div>
            <p className="text-xs text-muted-foreground">Weighted by probability</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Deal Velocity</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{dashboard?.deal_velocity_days || 0} days</div>
            <p className="text-xs text-muted-foreground">Avg. time to close</p>
          </CardContent>
        </Card>
      </div>

      {/* Pipeline & Category */}
      <div className="grid gap-6 md:grid-cols-2">
        {/* Pipeline Stages */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5" />
              Pipeline by Stage
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {Object.entries(dashboard?.stage_breakdown || {}).map(([stage, count]) => (
                <div key={stage} className="flex items-center justify-between">
                  <span className="capitalize text-sm">{stage.replace(/_/g, ' ')}</span>
                  <div className="flex items-center gap-2">
                    <div className="w-24 bg-secondary rounded-full h-2">
                      <div 
                        className="bg-primary h-2 rounded-full" 
                        style={{ width: `${Math.min((count / (dashboard?.pipeline_count || 1)) * 100, 100)}%` }}
                      />
                    </div>
                    <span className="text-sm font-medium w-8">{count}</span>
                  </div>
                </div>
              ))}
              {Object.keys(dashboard?.stage_breakdown || {}).length === 0 && (
                <p className="text-muted-foreground text-center py-4">No active deals</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Category Allocation */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <PieChart className="h-5 w-5" />
              Category Allocation (% Area)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {Object.entries(dashboard?.category_allocation || {}).sort((a, b) => b[1] - a[1]).map(([category, percent]) => (
                <div key={category} className="flex items-center justify-between">
                  <span className="capitalize text-sm">{category.replace(/_/g, ' ')}</span>
                  <div className="flex items-center gap-2">
                    <Progress value={percent} className="w-24 h-2" />
                    <span className="text-sm font-medium w-12">{percent}%</span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tenant Mix Analysis */}
      {tenantMix && selectedMall !== "all" && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Target className="h-5 w-5" />
              AI Tenant Mix Analysis
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-3 gap-6">
              <div>
                <h4 className="font-medium mb-2">Anchor Dependency</h4>
                <div className="text-3xl font-bold text-amber-500">{tenantMix.anchor_dependency_percent}%</div>
                <p className="text-xs text-muted-foreground mt-1">
                  {tenantMix.anchor_dependency_percent > 30 ? "HIGH - Consider diversifying" : "Healthy level"}
                </p>
              </div>
              
              <div>
                <h4 className="font-medium mb-2">Underrepresented Categories</h4>
                <div className="flex flex-wrap gap-1">
                  {tenantMix.underrepresented_categories?.map(cat => (
                    <Badge key={cat} variant="outline" className="capitalize">
                      {cat.replace(/_/g, ' ')}
                    </Badge>
                  ))}
                  {tenantMix.underrepresented_categories?.length === 0 && (
                    <span className="text-muted-foreground text-sm">None - well balanced</span>
                  )}
                </div>
              </div>

              <div>
                <h4 className="font-medium mb-2">White Space Opportunities</h4>
                {tenantMix.white_space_opportunities?.slice(0, 3).map((opp, idx) => (
                  <div key={idx} className="text-sm">
                    <span className="capitalize">{opp.category}</span>: {opp.available_units} units ({opp.total_area?.toLocaleString()} sqft)
                  </div>
                ))}
              </div>
            </div>

            {tenantMix.ai_reasoning && (
              <div className="mt-4 p-4 bg-muted/50 rounded-lg">
                <h4 className="font-medium mb-2">AI Analysis</h4>
                <p className="text-sm whitespace-pre-line">{tenantMix.ai_reasoning}</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Blocked Units */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Lock className="h-5 w-5" />
            Blocked Units ({blockedUnits.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {blockedUnits.length === 0 ? (
            <p className="text-muted-foreground text-center py-4">No blocked units</p>
          ) : (
            <div className="space-y-3">
              {blockedUnits.map(unit => (
                <div key={unit.id} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                  <div>
                    <span className="font-medium">{unit.unit_number}</span>
                    <span className="text-muted-foreground ml-2">Floor {unit.floor}</span>
                    {unit.blocked_for_brand && (
                      <Badge variant="secondary" className="ml-2">{unit.blocked_for_brand}</Badge>
                    )}
                  </div>
                  <div className="flex items-center gap-3">
                    <Badge variant={unit.status === "hard_block" ? "destructive" : "outline"}>
                      {unit.status.replace(/_/g, ' ')}
                    </Badge>
                    {unit.is_expired && (
                      <Badge variant="destructive">Expired</Badge>
                    )}
                    <Button size="sm" variant="outline" onClick={() => releaseUnit(unit.id)}>
                      <Unlock className="h-4 w-4 mr-1" /> Release
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Conversion Metrics */}
      {velocity && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5" />
              Pipeline Velocity (Last {velocity.period_days} Days)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-4 gap-6">
              <div className="text-center">
                <div className="text-3xl font-bold">{velocity.total_leads}</div>
                <p className="text-sm text-muted-foreground">Total Leads</p>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-green-500">{velocity.conversion_rate}%</div>
                <p className="text-sm text-muted-foreground">Conversion Rate</p>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-red-500">{velocity.loss_rate}%</div>
                <p className="text-sm text-muted-foreground">Loss Rate</p>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold">{formatCurrency(velocity.avg_deal_size)}</div>
                <p className="text-sm text-muted-foreground">Avg Deal Size</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
